// @flow
import * as React from 'react';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import createFocusOnError from 'final-form-focus';
import classNames from 'classnames/bind';
import { isNil } from 'ramda';

import { useReactiveVar } from '@apollo/client';

import { FormField } from '../../../hocs/final-form';
import useMediaQuery from '../../../hooks/use-media-query';

import ApolloLightbox from '../../apollo-lightbox';
import InputField from '../../../basic-components/input-field';
import Button from '../../../basic-components/button';
import SelectField from '../../../basic-components/select-field';
import FieldSet from '../../field-set';
import FieldSetDescription from '../../field-set-description';
import ThreeColumnsLayout from '../../../basic-components/three-columns-layout';
import Link from '../../../basic-components/link';

import t from '../locale';
import cs from '../styles.pcss';

import settings from '../../../settings';
import { gpsrConfigsVar } from '../../../utils/apollo-cache';
import { emailValidate } from '../../../typings/utils';
import { contactURLValidate } from '../../../typings/utils';
import { getOptions } from '../../../store/gpsr';

const cx = classNames.bind(cs);

const focusOnErrors = createFocusOnError();
const renderFooter =
  (
    handleSubmit?: any,
    submitError?: boolean,
    submitting?: boolean,
    mediumScreen: boolean = true
  ) =>
  (closeOverlay: () => Promise<void>) =>
    (
      <ThreeColumnsLayout
        left={
          <Button
            onClick={closeOverlay}
            mode="secondary"
            size={mediumScreen ? 'medium' : 'inModal'}
          >
            {t('buttons.close')}
          </Button>
        }
        center={
          <Button
            loading={submitting}
            onClick={handleSubmit}
            size={mediumScreen ? 'medium' : 'inModal'}
          >
            {t('buttons.save')}
          </Button>
        }
        className={cs.footerWrapper}
      />
    );
type Props = {
  manufacturer: any,
  onSave: (data: any) => void,
};
function GpsrManufactureLightbox({ manufacturer, onSave }: Props): React.Node {
  const veryLargeScreen = useMediaQuery(`(min-width: 1000px)`);
  const mediumScreen = useMediaQuery(`(min-width: 600px)`);

  const countries = getOptions(useReactiveVar(gpsrConfigsVar), 'country');
  const validateEmail = (email) => {
    if (!email || !email.trim()) {
      return null;
    }
    if (!emailValidate(email)) {
      return t('manufacturer.fields.email.validate.incorrect');
    }
    return null;
  };
  const validateContactURL = (url) => {
    if (!url || !url.trim()) {
      return null;
    }
    if (!contactURLValidate(url)) {
      return t('manufacturer.fields.contactUrl.validate.incorrect');
    }
    return null;
  };

  return (
    <Form
      initialValues={manufacturer}
      initialValuesEqual={() => false}
      subscription={{
        submitting: true,
        submitError: true,
      }}
      mutators={{ ...arrayMutators }}
      decorators={[focusOnErrors]}
      onSubmit={(data) => {
        onSave(data);
      }}
    >
      {({ handleSubmit, submitError, submitting }) => (
        <ApolloLightbox
          id="GpsrManufacture"
          header={t('manufacturer.title')}
          footer={renderFooter(
            handleSubmit,
            submitError,
            submitting,
            mediumScreen
          )}
        >
          <form className={cx(cs.wrapper)} onSubmit={(e) => e.preventDefault()}>
            <FieldSet>
              <FieldSetDescription>
                {t('manufacturer.formDescription')}
                <Link
                  target="_blank"
                  href={
                    settings.references.gpsrLinks('manufacturer')
                  }
                  className={cs.link}
                  noUnderline={false}
                  onClick={() => {}}
                >
                  {t('linkText')}
                </Link>
              </FieldSetDescription>
              <FormField
                name="companyName"
                validate={(val) =>
                  !val || !val.trim()
                    ? t('manufacturer.fields.companyName.validate.empty')
                    : null
                }
                Component={InputField}
                label={t('manufacturer.fields.companyName.label')}
                noGrid={!veryLargeScreen}
                maxLength={100}
                useDefaultValue
              />
              <FormField
                name="country"
                validate={(val) =>
                  isNil(val)
                    ? t('manufacturer.fields.country.validate.empty')
                    : null
                }
                Component={SelectField}
                options={countries}
                label={t('manufacturer.fields.country.label')}
                noGrid={!veryLargeScreen}
                onChange={({ value }) => value}
                searchable={false}
                useDefaultValue
              />
              <FormField
                name="street1"
                validate={(val) =>
                  !val || !val.trim()
                    ? t('manufacturer.fields.street1.validate.empty')
                    : null
                }
                Component={InputField}
                label={t('manufacturer.fields.street1.label')}
                noGrid={!veryLargeScreen}
                maxLength={180}
                useDefaultValue
              />
              <FormField
                name="street2"
                Component={InputField}
                label={t('manufacturer.fields.street2.label')}
                noGrid={!veryLargeScreen}
                maxLength={180}
                useDefaultValue
              />
              <FormField
                name="cityName"
                validate={(val) =>
                  !val || !val.trim()
                    ? t('manufacturer.fields.cityName.validate.empty')
                    : null
                }
                Component={InputField}
                label={t('manufacturer.fields.cityName.label')}
                noGrid={!veryLargeScreen}
                maxLength={64}
                useDefaultValue
              />
              <FormField
                name="postalCode"
                validate={(val) =>
                  !val
                    ? t('manufacturer.fields.postalCode.validate.empty')
                    : null
                }
                Component={InputField}
                label={t('manufacturer.fields.postalCode.label')}
                noGrid={!veryLargeScreen}
                maxLength={9}
                useDefaultValue
              />
              <FormField
                name="stateOrProvince"
                validate={(val) =>
                  !val || !val.trim()
                    ? t('manufacturer.fields.stateOrProvince.validate.empty')
                    : null
                }
                Component={InputField}
                label={t('manufacturer.fields.stateOrProvince.label')}
                noGrid={!veryLargeScreen}
                maxLength={64}
                useDefaultValue
              />
              <FormField
                name="phone"
                Component={InputField}
                label={t('manufacturer.fields.phone.label')}
                noGrid={!veryLargeScreen}
                maxLength={64}
                mask="number"
                useDefaultValue
              />
              <FormField
                name="email"
                validate={(val) => validateEmail(val)}
                Component={InputField}
                label={t('manufacturer.fields.email.label')}
                noGrid={!veryLargeScreen}
                maxLength={180}
                useDefaultValue
              />
              <FormField
                name="contactUrl"
                validate={(val) => validateContactURL(val)}
                Component={InputField}
                label={t('manufacturer.fields.contactUrl.label')}
                noGrid={!veryLargeScreen}
                maxLength={180}
                useDefaultValue
              />
            </FieldSet>
          </form>
        </ApolloLightbox>
      )}
    </Form>
  );
}

export default GpsrManufactureLightbox;
